

import { Button, Alert } from "@a2z/ui";

import { useEffect, useState } from "react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import type { StoreSubscriptionLedgerResponse } from "@/lib/api/types";
import {
  STORE_SUBSCRIPTION_ALLOCATION_KIND,
  STORE_SUBSCRIPTION_SCHEDULE_STATUS,
} from "@/lib/api/types";
import { ApiJsonClientError } from "@/lib/query/core/api-json-client";
import { useStoreSubscriptionLedgerQuery } from "@/modules/stores/services/queries/useStoreSubscriptionLedgerQuery";

type Props = {
  storeId: string;
  locale: string;
  triggerVariant?: "icon" | "menu";
  triggerClassName?: string;
  triggerLabel?: string;
  onOpen?: () => void;
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  hideTrigger?: boolean;
};

export function StoreScheduleLedgerModal({
  storeId,
  locale,
  triggerVariant = "icon",
  triggerClassName,
  triggerLabel,
  onOpen,
  isOpen: controlledOpen,
  onOpenChange,
  hideTrigger,
}: Props) {
  const t = useTranslations();
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = controlledOpen ?? internalOpen;
  const setOpen = onOpenChange ?? setInternalOpen;
  const { data, isLoading, error: ledgerError, refetch } =
    useStoreSubscriptionLedgerQuery({
      storeId: isOpen ? storeId : "",
    });

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, setOpen]);

  const formatDate = (value: string | null) => {
    if (!value) {
      return "-";
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return "-";
    }
    return new Intl.DateTimeFormat(locale, { dateStyle: "medium" }).format(date);
  };

  const formatAmount = (amount: number, currency: string) => {
    try {
      return new Intl.NumberFormat(locale, {
        style: "currency",
        currency: currency || "EGP",
        maximumFractionDigits: 0,
      }).format(amount);
    } catch {
      return `${amount} ${currency ?? ""}`.trim();
    }
  };

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    const timer = window.setTimeout(() => {
      void refetch();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [isOpen, refetch]);

  const error =
    ledgerError instanceof ApiJsonClientError
      ? ledgerError.message || t(keys.errors.generic)
      : null;
  const ledger: StoreSubscriptionLedgerResponse | null = data ?? null;

  const openModal = () => {
    setOpen(true);
    onOpen?.();
  };

  const label = triggerLabel ?? t(keys.admin.storeSubscriptionsScheduleLedgerAction);
  const triggerClasses =
    triggerVariant === "menu"
      ? "w-full text-left text-xs font-semibold text-[color:var(--text-secondary)]"
      : "group relative inline-flex h-9 w-9 items-center justify-center rounded-full border border-[color:var(--border-light)] text-[color:var(--text-muted)] transition hover:border-[color:var(--primary-300)]";

  return (
    <>
      {hideTrigger ? null : (
        <Button variant="ghost"
          type="button"
          onClick={openModal}
          className={`${triggerClasses}${triggerClassName ? ` ${triggerClassName}` : ""}`}
          aria-label={label}
        >
          {triggerVariant === "menu" ? (
            label
          ) : (
            <>
              <svg
                aria-hidden="true"
                viewBox="0 0 24 24"
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M4 6h16" />
                <path d="M4 12h16" />
                <path d="M4 18h10" />
              </svg>
              <span className="pointer-events-none absolute right-0 top-full z-10 mt-2 whitespace-nowrap rounded-md bg-[color:var(--text-primary)] px-2 py-1 text-[13px] font-semibold text-[color:var(--surface)] opacity-0 shadow-[var(--shadow-sm)] transition-opacity group-hover:opacity-100">
                {label}
              </span>
            </>
          )}
        </Button>
      )}

      {isOpen ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 pt-10 pb-[max(2.5rem,env(safe-area-inset-bottom))]"
          role="dialog"
          aria-modal="true"
          onClick={() => setOpen(false)}
        >
          <div
            className="max-h-full w-full max-w-3xl overflow-y-auto rounded-2xl bg-[color:var(--surface)] p-6 shadow-[var(--shadow-md)]"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-base font-semibold text-[color:var(--text-primary)]">
                  {t(keys.admin.storeSubscriptionsScheduleLedgerTitle)}
                </h2>
                <p className="mt-1 text-xs text-[color:var(--text-muted)]">
                  {t(keys.admin.storeSubscriptionsScheduleLedgerHint)}
                </p>
              </div>
              <Button variant="ghost"
                type="button"
                onClick={() => setOpen(false)}
                className="text-xs font-semibold text-[color:var(--text-muted)]"
              >
                {t(keys.common.close)}
              </Button>
            </div>

            <div className="mt-5 grid gap-4">
              {error ? (
                <Alert message={error} helperText={t(keys.errors.recovery)} />
              ) : null}
              {isLoading ? (
                <div className="flex items-center gap-2 text-xs text-[color:var(--text-muted)]">
                  <span className="inline-flex h-4 w-4 animate-spin rounded-full border-2 border-[color:var(--border-light)] border-t-transparent" />
                  {t(keys.common.loading)}
                </div>
              ) : ledger ? (
                <>
                  {ledger.creditBalance > 0 ? (
                    <div className="rounded-xl border border-[color:var(--border-light)] bg-[color:var(--surface)] px-4 py-3 text-xs">
                      <div className="text-[13px] font-semibold text-[color:var(--text-secondary)]">
                        {t(keys.admin.storeSubscriptionsCreditBalance)}
                      </div>
                      <div className="mt-1 text-sm font-semibold text-[color:var(--text-primary)]">
                        {formatAmount(ledger.creditBalance, ledger.currency)}
                      </div>
                      {ledger.creditAllocations.length > 0 ? (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {ledger.creditAllocations.map((allocation) => (
                            <span
                              key={allocation.id}
                              className="rounded-full border border-[color:var(--border-light)] px-2 py-0.5 text-[13px] text-[color:var(--text-secondary)]"
                            >
                              {formatAmount(allocation.remainingAmount, ledger.currency)} •{" "}
                              {formatDate(allocation.paymentPaidAt)}
                            </span>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  ) : null}

                  <div className="grid gap-3">
                    {ledger.schedules.map((item) => {
                      const remaining = Math.max(
                        item.schedule.amountDue - item.schedule.paidAmount,
                        0
                      );
                      const statusClass =
                        item.schedule.status ===
                        STORE_SUBSCRIPTION_SCHEDULE_STATUS.PAID
                          ? "bg-[color:var(--success-100)] text-[color:var(--success-700)]"
                          : item.schedule.status ===
                              STORE_SUBSCRIPTION_SCHEDULE_STATUS.PARTIAL
                            ? "bg-[color:var(--warning-100)] text-[color:var(--warning-700)]"
                            : "bg-[color:var(--border-light)] text-[color:var(--text-secondary)]";
                      return (
                        <div
                          key={item.schedule.id}
                          className="rounded-xl border border-[color:var(--border-light)] px-4 py-3 text-xs"
                        >
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <div className="text-[13px] text-[color:var(--text-muted)]">
                              {formatDate(item.schedule.dueDate)}
                            </div>
                            <span className={`rounded-full px-2 py-0.5 text-[13px] font-semibold ${statusClass}`}>
                              {item.schedule.status}
                            </span>
                          </div>
                          <div className="mt-2 grid gap-2 sm:grid-cols-3">
                            <div>
                              <div className="text-[13px] text-[color:var(--text-muted)]">
                                {t(keys.admin.subscriptionScheduleDueAmount)}
                              </div>
                              <div className="text-sm font-semibold text-[color:var(--text-primary)]">
                                {formatAmount(item.schedule.amountDue, item.schedule.currency)}
                              </div>
                            </div>
                            <div>
                              <div className="text-[13px] text-[color:var(--text-muted)]">
                                {t(keys.admin.subscriptionSchedulePaid)}
                              </div>
                              <div className="text-sm font-semibold text-[color:var(--text-primary)]">
                                {formatAmount(item.schedule.paidAmount, item.schedule.currency)}
                              </div>
                            </div>
                            <div>
                              <div className="text-[13px] text-[color:var(--text-muted)]">
                                {t(keys.admin.subscriptionScheduleRemaining)}
                              </div>
                              <div className="text-sm font-semibold text-[color:var(--text-primary)]">
                                {formatAmount(remaining, item.schedule.currency)}
                              </div>
                            </div>
                          </div>
                          {item.allocations.length > 0 ? (
                            <div className="mt-3 flex flex-wrap gap-2">
                              {item.allocations
                                .filter(
                                  (allocation) =>
                                    allocation.kind ===
                                    STORE_SUBSCRIPTION_ALLOCATION_KIND.SCHEDULE
                                )
                                .map((allocation) => (
                                  <span
                                    key={allocation.id}
                                    className="rounded-full border border-[color:var(--border-light)] px-2 py-0.5 text-[13px] text-[color:var(--text-secondary)]"
                                  >
                                    {formatAmount(
                                      allocation.amount,
                                      item.schedule.currency
                                    )} • {formatDate(allocation.paymentPaidAt)}
                                  </span>
                                ))}
                            </div>
                          ) : (
                            <div className="mt-3 text-[13px] text-[color:var(--text-muted)]">
                              {t(keys.common.emptyTitle)}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
